if (!customElements.get("support-cinema")) {
  customElements.define(
    "support-cinema",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.controller = new AbortController();
        const { signal } = this.controller;
        const video = this.querySelector("video");
        const retry = this.querySelector("[data-cinema-retry]");
        const status = this.querySelector("[data-cinema-status]");
        const stage = this.querySelector("[data-cinema-stage]");
        const endLink = this.querySelector("[data-cinema-end-link]");
        if (!video || !retry || !status || !stage) return;
        this.video = video;
        const labels = this.dataset;
        const mobile = window.matchMedia("(max-width: 749px)");
        let restorePosition = null;
        const ready = () =>
          video.readyState >= 1 &&
          Number.isFinite(video.duration) &&
          video.duration > 0;
        const sync = () => {
          if (endLink)
            endLink.hidden =
              !video.ended || !video.paused || video.seeking || !ready();
        };
        const metadata = () => {
          if (!ready()) return;
          const restoreFocus = document.activeElement === retry;
          retry.hidden = true;
          retry.disabled = false;
          status.textContent = "";
          this.setAttribute("data-ready", "");
          if (restorePosition !== null) {
            video.currentTime = Math.min(
              video.duration,
              restorePosition * video.duration,
            );
            restorePosition = null;
          }
          sync();
          if (restoreFocus) video.focus({ preventScroll: true });
        };
        const seek = (value) => {
          if (!ready()) return;
          video.pause();
          video.currentTime = Math.max(0, Math.min(video.duration, value));
          sync();
        };
        retry.addEventListener(
          "click",
          async () => {
            retry.disabled = true;
            status.textContent = labels.loadingLabel;
            video.preload = "metadata";
            video.load();
            try {
              await video.play();
              status.textContent = "";
            } catch (error) {
              if (error.name !== "AbortError") {
                retry.hidden = false;
                retry.disabled = false;
                status.textContent = video.error
                  ? labels.errorLabel
                  : labels.playbackErrorLabel;
              }
            }
          },
          { signal },
        );
        ["loadedmetadata", "durationchange"].forEach((event) =>
          video.addEventListener(event, metadata, { signal }),
        );
        ["timeupdate", "play", "pause", "ended", "seeking", "seeked"].forEach(
          (event) => video.addEventListener(event, sync, { signal }),
        );
        video.addEventListener(
          "error",
          () => {
            if (endLink) endLink.hidden = true;
            this.removeAttribute("data-ready");
            retry.hidden = false;
            retry.disabled = false;
            status.textContent = labels.errorLabel;
          },
          { signal, capture: true },
        );

        const pointer = window.matchMedia("(hover: hover) and (pointer: fine)");
        let drag = null;
        let suppressClickUntil = 0;
        const stopDrag = () => {
          if (this.hasAttribute("data-dragging"))
            suppressClickUntil = performance.now() + 400;
          drag = null;
          this.removeAttribute("data-dragging");
        };
        stage.addEventListener(
          "click",
          (event) => {
            if (performance.now() >= suppressClickUntil) return;
            event.preventDefault();
            event.stopPropagation();
          },
          { signal, capture: true },
        );
        stage.addEventListener(
          "pointerdown",
          (event) => {
            if (
              !this.hasAttribute("data-enable-drag") ||
              !ready() ||
              video.ended ||
              !pointer.matches ||
              event.pointerType !== "mouse" ||
              event.button !== 0
            )
              return;
            const box = video.getBoundingClientRect();
            if (event.clientY >= box.bottom - 72) return;
            drag = {
              x: event.clientX,
              time: video.currentTime,
              width: box.width,
              id: event.pointerId,
            };
          },
          { signal },
        );
        stage.addEventListener(
          "pointermove",
          (event) => {
            if (!drag || drag.id !== event.pointerId) return;
            if (
              !this.hasAttribute("data-dragging") &&
              Math.abs(event.clientX - drag.x) < 6
            )
              return;
            if (!this.hasAttribute("data-dragging")) {
              stage.setPointerCapture(event.pointerId);
              this.setAttribute("data-dragging", "");
            }
            event.preventDefault();
            seek(
              drag.time +
                ((event.clientX - drag.x) / drag.width) * video.duration,
            );
          },
          { signal },
        );
        [
          "pointerup",
          "pointercancel",
          "lostpointercapture",
          "pointerleave",
        ].forEach((event) =>
          stage.addEventListener(event, stopDrag, { signal }),
        );
        document.addEventListener(
          "visibilitychange",
          () => {
            if (document.hidden) video.pause();
          },
          { signal },
        );
        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries[0].isIntersecting) {
                video.pause();
                stopDrag();
              }
            },
            { threshold: 0 },
          );
          this.observer.observe(stage);
        }
        const selectSource = () => {
          const source = mobile.matches
            ? labels.mobileSource
            : labels.desktopSource;
          video.poster = mobile.matches
            ? labels.mobilePoster
            : labels.desktopPoster;
          if (!source || video.getAttribute("src") === source) return;
          if (ready())
            restorePosition = video.ended
              ? 0
              : video.currentTime / video.duration;
          video.pause();
          stopDrag();
          if (endLink) endLink.hidden = true;
          this.removeAttribute("data-ready");
          retry.hidden = true;
          retry.disabled = false;
          status.textContent = "";
          video.preload = "none";
          video.src = source;
        };
        mobile.addEventListener("change", selectSource, { signal });
        selectSource();
        metadata();
      }

      disconnectedCallback() {
        this.video?.pause();
        this.controller?.abort();
        this.observer?.disconnect();
      }
    },
  );
}
